import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { DeleteIcon, PlusIcon, ResetIcon } from '@/components/Icons'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'
import CustomRichTextEditor from '../CustomRichTextEditor'
import { useFormContext, useFieldArray, Controller } from 'react-hook-form'
import type {
  EventFormDataType,
  eventHighlightSchema,
} from '@/schemas/eventForm.schema'
import { ErrorMessage } from '../ErrorMessage'
import { MAX_EVENT_HIGHLIGHTS_LIMIT } from '@/constants'
import { DatePicker } from '../ui/date-picker'
import { TimePicker } from '../ui/time-picker'
import { FileUpload } from '../ui/file-upload'
import { Highlighter } from 'lucide-react'
import { normalizeDate } from '@/utils/dateUtils'
import { eventHighLightImageProperties } from '@/constants/event'
import type z from 'zod'

interface EventHighlightsProps {
  handleImageSelect: (files: File | null, index: number) => Promise<void>
}

export default function EventHighlights({
  handleImageSelect,
}: EventHighlightsProps) {
  const {
    control,
    watch,
    setValue,
    clearErrors,
    trigger,
    formState: { errors },
  } = useFormContext<EventFormDataType>()

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'eventHighlights',
  })

  const maxAllowedHighlights = MAX_EVENT_HIGHLIGHTS_LIMIT

  const handleAddHighlight = () => {
    if (fields.length >= maxAllowedHighlights) return
    append({
      id: Date.now().toString(),
      title: '',
      shortDescription: '',
      isNew: true,
    })
  }

  const handleStartDateDisable = (date: Date, index: number) => {
    const today = normalizeDate(new Date())
    const endDate = watch(`eventHighlights.${index}.endDate`)
    const checkDate = normalizeDate(date)

    if (checkDate < today) return true

    if (endDate) {
      const normalizedEndDate = normalizeDate(endDate)
      return checkDate > normalizedEndDate
    }

    return false
  }

  const handleEndDateDisable = (date: Date, index: number) => {
    const today = normalizeDate(new Date())
    const startDate = watch(`eventHighlights.${index}.startDate`)
    const earliestAllowed = startDate ? normalizeDate(startDate) : today

    const checkDate = normalizeDate(date)
    return checkDate < earliestAllowed
  }

  const clearHighlightErrors = (index: number) => {
    clearErrors([
      `eventHighlights.${index}.title`,
      `eventHighlights.${index}.shortDescription`,
      `eventHighlights.${index}.startDate`,
      `eventHighlights.${index}.endDate`,
      `eventHighlights.${index}.startTime`,
      `eventHighlights.${index}.endTime`,
      `eventHighlights.${index}.eventImage`,
    ])
  }

  const handleRemoveHighlight = (index: number) => {
    if (fields.length <= 1) return
    clearHighlightErrors(index)
    remove(index)
  }

  const handleHighlightReset = (index: number) => {
    setValue(`eventHighlights.${index}.title`, '')
    setValue(`eventHighlights.${index}.shortDescription`, '')
    setValue(`eventHighlights.${index}.startDate`, undefined)
    setValue(`eventHighlights.${index}.endDate`, undefined)
    setValue(`eventHighlights.${index}.startTime`, undefined)
    setValue(`eventHighlights.${index}.endTime`, undefined)
    setValue(`eventHighlights.${index}.eventImage`, null)
    setValue(`eventHighlights.${index}.imageFile`, undefined)
    setValue(`eventHighlights.${index}.isNew`, true)

    clearHighlightErrors(index)
  }

  const handleFileSelect = (file: File | null, index: number) => {
    if (file === null) {
      setValue(`eventHighlights.${index}.eventImage`, null)
      setValue(`eventHighlights.${index}.imageFile`, undefined)
      clearErrors(`eventHighlights.${index}.eventImage`)
    } else {
      handleImageSelect(file, index)
    }
  }

  const getHighLightImagePreviewUrl = (
    highlight: z.infer<typeof eventHighlightSchema>
  ) => {
    return highlight?.imageFile
      ? URL.createObjectURL(highlight.imageFile)
      : highlight?.eventImage || undefined
  }

  return (
    <>
      <CardHeader className="pb-4 border-t border-b border-black/6 bg-ss-neutral-25 flex flex-row items-center gap-2 px-4 py-3">
        <Highlighter className="h-5 w-5 text-ss-neutral-600" />
        <CardTitle className="text-lg font-semibold text-ss-neutral-950 flex gap-1 items-center">
          Event Highlights
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 space-y-4">
        <section className="space-y-4">
          {fields.map((field, index) => (
            <div key={field.id} className="space-y-4">
              {/* Event Highlight Header */}
              <div className="bg-ss-neutral-25 box-border flex flex-col gap-2.5 items-start justify-start px-3 py-1.5 relative rounded border-r-[#cdd1d9] w-full">
                <div className="flex gap-8 items-start justify-start w-full">
                  <div className="flex gap-1.5 grow items-center justify-between">
                    <h3 className="font-bold text-ss-neutral-950 text-base leading-[22px]">
                      Event Highlight {index + 1}
                    </h3>
                    <div className="flex align-middle items-center">
                      {fields.length === 1 &&
                        (() => {
                          const highlight = watch('eventHighlights')?.[0] || {}
                          const hasAnyValue = !!(
                            highlight.title ||
                            highlight.shortDescription ||
                            highlight.startDate ||
                            highlight.endDate ||
                            highlight.eventImage ||
                            highlight.imageFile
                          )
                          return (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <Button
                                    disabled={
                                      fields.length >= maxAllowedHighlights ||
                                      !hasAnyValue
                                    }
                                    type="button"
                                    variant="ghost"
                                    aria-label="Reset event highlight fields"
                                    className="p-0 flex text-ss-blue-900 stroke-1 gap-1 h-auto justify-end disabled:bg-transparent disabled:text-ss-neutral-300 hover:text-ss-blue-900 hover:bg-transparent mr-2"
                                    onClick={() => {
                                      if (fields.length === 1) {
                                        handleHighlightReset(index)
                                      }
                                    }}
                                  >
                                    <ResetIcon
                                      className="size-5 hover:text-ss-blue-900"
                                      aria-hidden="true"
                                    />
                                    <span className="font-medium  text-sm leading-4.5   hover:text-ss-blue-900 disabled:text-ss-neutral-200">
                                      Reset
                                    </span>
                                  </Button>
                                </span>
                              </TooltipTrigger>
                            </Tooltip>
                          )
                        })()}
                      {index === fields.length - 1 && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <Button
                                disabled={fields.length >= maxAllowedHighlights}
                                type="button"
                                variant="ghost"
                                onClick={handleAddHighlight}
                                aria-label="Add more event highlights"
                                className="p-0 flex text-ss-blue-600 stroke-1 gap-1 h-auto justify-end disabled:bg-transparent disabled:text-ss-neutral-300 hover:text-ss-blue-700 hover:bg-transparent"
                              >
                                <PlusIcon
                                  className="size-5 hover:text-ss-blue-700"
                                  aria-hidden="true"
                                />
                                <span className="font-medium  text-sm leading-4.5   hover:text-ss-blue-700 disabled:text-ss-neutral-200">
                                  Add More
                                </span>
                              </Button>
                            </span>
                          </TooltipTrigger>
                          {fields.length >= maxAllowedHighlights && (
                            <TooltipContent>
                              Maximum {MAX_EVENT_HIGHLIGHTS_LIMIT} event
                              highlights can be created
                            </TooltipContent>
                          )}
                        </Tooltip>
                      )}
                      {fields.length > 1 && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <Button
                                type="button"
                                variant="ghost"
                                onClick={() => handleRemoveHighlight(index)}
                                className="gap-1 flex p-0 h-auto disabled:opacity-50 disabled:bg-transparent disabled:cursor-not-allowed ml-2"
                                aria-label={`Delete Event Highlight ${index + 1}`}
                              >
                                <DeleteIcon
                                  strokeWidth={1.4}
                                  className="h-5 w-5 text-ss-critical-200"
                                  aria-hidden="true"
                                />
                                <span className="font-medium text-ss-critical-200 text-sm leading-4.5 ">
                                  Delete
                                </span>
                              </Button>
                            </span>
                          </TooltipTrigger>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Event Highlight Form Fields */}
              <div className="space-y-4">
                <div className="flex gap-4 w-full sm:flex-row flex-col">
                  <div className="flex flex-col flex-1">
                    <Label
                      variant="required"
                      htmlFor={`eventHighlights.${index}.title`}
                      className="text-sm font-medium text-ss-neutral-950 leading-4.5  mb-1"
                    >
                      Title
                    </Label>
                    <Controller
                      name={`eventHighlights.${index}.title`}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <>
                          <Input
                            {...field}
                            id={`highlight-title-${index}`}
                            required
                            type="text"
                            placeholder="Enter highlight title"
                            className="h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20"
                          />
                          {error && <ErrorMessage error={error.message!} />}
                        </>
                      )}
                    />
                  </div>
                </div>

                {/* Description and What's Included */}
                <div className="flex gap-4 w-full sm:flex-row flex-col">
                  <div className="flex-1 flex flex-col min-h-px min-w-px">
                    <Label
                      variant="required"
                      htmlFor={`eventHighlights.${index}.shortDescription`}
                      className="text-sm font-medium text-ss-neutral-950"
                    >
                      Event Highlight Description
                    </Label>
                    <Controller
                      name={`eventHighlights.${index}.shortDescription`}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <div className="relative">
                          <CustomRichTextEditor
                            value={field.value}
                            onChange={value =>
                              field.onChange(value === '<p></p>' ? '' : value)
                            }
                            placeholder="Add a clear and engaging description of your event."
                            className={error ? 'border-ss-critical-200' : ''}
                          />
                          {error && (
                            <p className="text-sm text-ss-critical-200 mt-1">
                              {error.message}
                            </p>
                          )}
                        </div>
                      )}
                    />
                  </div>
                </div>

                {/* Start Date & Time / End Date & Time */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Start Date & Time */}
                  <div className="space-y-3">
                    {/* Start Date */}
                    <div className="space-y-1">
                      <Label
                        htmlFor={`eventHighlights.${index}.startDate`}
                        className="text-sm font-medium text-ss-neutral-950"
                      >
                        Start Date
                      </Label>
                      <DatePicker
                        value={watch(`eventHighlights.${index}.startDate`)}
                        onChange={value => {
                          setValue(
                            `eventHighlights.${index}.startDate`,
                            value,
                            {
                              shouldValidate: true,
                            }
                          )
                          trigger(`eventHighlights.${index}.endDate`)
                        }}
                        placeholder="Select start date"
                        className="w-full"
                        disableDate={date =>
                          handleStartDateDisable(date, index)
                        }
                      />
                      {errors?.eventHighlights?.[index]?.startDate && (
                        <p
                          className="text-sm text-ss-critical-200 mt-1"
                          role="alert"
                        >
                          {errors.eventHighlights[index]?.startDate?.message}
                        </p>
                      )}
                    </div>

                    {/* Start Time */}
                    <div className="space-y-1">
                      <Label
                        htmlFor={`eventHighlights.${index}.startTime`}
                        className="text-sm font-medium text-ss-neutral-950"
                      >
                        Start Time
                      </Label>
                      <TimePicker
                        value={watch(`eventHighlights.${index}.startTime`)}
                        onChange={value =>
                          setValue(
                            `eventHighlights.${index}.startTime`,
                            value,
                            {
                              shouldValidate: true,
                            }
                          )
                        }
                      />
                      {errors?.eventHighlights?.[index]?.startTime && (
                        <p
                          className="text-sm text-ss-critical-200 mt-1"
                          role="alert"
                        >
                          {errors.eventHighlights[index]?.startTime?.message}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* End Date & Time */}
                  <div className="space-y-3">
                    {/* End Date */}
                    <div className="space-y-1">
                      <Label
                        htmlFor={`eventHighlights.${index}.endDate`}
                        className="text-sm font-medium text-ss-neutral-950"
                      >
                        End Date
                      </Label>
                      <DatePicker
                        value={watch(`eventHighlights.${index}.endDate`)}
                        onChange={value => {
                          setValue(`eventHighlights.${index}.endDate`, value, {
                            shouldValidate: true,
                          })
                          trigger(`eventHighlights.${index}.startDate`)
                        }}
                        placeholder="Select end date"
                        className="w-full"
                        disableDate={date => handleEndDateDisable(date, index)}
                      />
                      {errors?.eventHighlights?.[index]?.endDate && (
                        <p
                          className="text-sm text-ss-critical-200 mt-1"
                          role="alert"
                        >
                          {errors.eventHighlights[index]?.endDate?.message}
                        </p>
                      )}
                    </div>

                    {/* End Time */}
                    <div className="space-y-1">
                      <Label
                        htmlFor={`eventHighlights.${index}.endTime`}
                        className="text-sm font-medium text-ss-neutral-950"
                      >
                        End Time
                      </Label>
                      <TimePicker
                        value={watch(`eventHighlights.${index}.endTime`)}
                        onChange={value =>
                          setValue(`eventHighlights.${index}.endTime`, value, {
                            shouldValidate: true,
                          })
                        }
                      />
                      {errors?.eventHighlights?.[index]?.endTime && (
                        <p
                          className="text-sm text-ss-critical-200 mt-1"
                          role="alert"
                        >
                          {errors.eventHighlights[index]?.endTime?.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-1 max-w-[541px]">
                  <Label className="text-sm font-medium text-ss-neutral-950">
                    Event Highlight Image
                  </Label>
                  <Controller
                    name={`eventHighlights.${index}.eventImage`}
                    control={control}
                    render={({ fieldState: { error } }) => {
                      const highlight = watch(`eventHighlights.${index}`)
                      const previewUrl = getHighLightImagePreviewUrl(highlight)

                      return (
                        <>
                          <FileUpload
                            key={`highlight-image-${index}-${highlight?.imageFile || highlight?.eventImage || 'empty'}`}
                            onFileSelect={file => handleFileSelect(file, index)}
                            acceptedFileTypes={
                              eventHighLightImageProperties.acceptedFileTypes
                            }
                            maxFileSize={
                              eventHighLightImageProperties.maxFileSize
                            }
                            hint={eventHighLightImageProperties.hint}
                            previewImage={previewUrl}
                            previewImageHeadText="Event Highlight Image"
                            previewImageSubText="Current Image"
                          />
                          {error && (
                            <p className="text-sm text-ss-critical-200 mt-1">
                              {error.message}
                            </p>
                          )}
                        </>
                      )
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </section>
      </CardContent>
    </>
  )
}
